<template>
  <AppChatMessage class="mb-6" should-display-error-message>
    <template #message-author>
      <v-img
        src="@/assets/img/ai_assistant_ico.svg"
        :width="32"
        :height="32"
      />
    </template>

    <span class="font-weight-bold">
            {{ $t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.pdfContainsNoText') }}
          </span>

    <p class="mt-2">
      {{ $t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.ocrProcessExplanation') }}
      <a class="app-link learnMoreLink font-weight-medium" :href="learnMoreLink" target="_blank">
        {{ $t("common.learnMore") }}
      </a>
    </p>

    <app-button size="small"
                fullWidth
                type="outlined"
                variant="neutral"
                :disabled="status !== 'available'"
                @click="$emit('launch-conversion')"
    >
      <app-material-icon name="cached" />
      {{ $t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.launchConversion') }}
    </app-button>
  </AppChatMessage>

  <AppChatMessage
    v-if="status !== 'available'"
    :class="{ 'ml-9': !statusIsError }"
    class="mb-2"
    :should-display-error-message="statusIsError"
  >
    <template v-if="statusIsError" #message-author>
      <v-img
        src="@/assets/img/ai_assistant_ico.svg"
        :width="32"
        :height="32"
      />
    </template>

    <div class="d-flex column-gap-2 align-center">
      <template v-if="status === 'finished'">
        <app-material-icon name="check" color="#068925" />
      </template>
      <span class="font-weight-bold">
        {{ processInfosTitle }}
      </span>
    </div>
    <div v-if="statusIsError" class="mt-2">
      {{ $t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.errorOccurredInfos') }}
    </div>

    <v-divider class="my-4" />

    <div v-if="!statusIsError">
      <div class="d-flex column-gap-2 align-center">
        <v-progress-circular
          v-if="status === 'ocrPending'"
          style="width:20px; height:20px;"
          indeterminate size="64"
          color="icon-protege"
        />
        <app-material-icon v-else name="check" color="#068925" />
        {{ $t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.documentConversion') }}
      </div>
      <div v-if="status !=='ocrPending'" class="d-flex column-gap-2 align-center mt-3">
        <v-progress-circular
          v-if="status === 'summaryPending'"
          style="width:20px; height:20px;"
          indeterminate size="64"
          color="icon-protege"
        />
        <app-material-icon v-else name="check" color="#068925" />
        {{ $t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.summaryGeneration') }}
      </div>
    </div>

    <div v-else-if="status === 'errorOnOcr'" class="d-flex column-gap-2 align-center">
      <app-material-icon name="close" color="#D10027" />
      {{ $t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.errorOnOcr') }}
    </div>

    <div v-else-if="status === 'errorOnSummary'">
      <div class="d-flex column-gap-2 align-center">
        <app-material-icon name="check" color="#068925" />
        {{ $t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.documentConversion') }}
      </div>
      <div class="d-flex column-gap-2 align-center mt-3">
        <app-material-icon name="close" color="#D10027" />
        {{ $t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.errorOnSummary') }}
      </div>
    </div>
  </AppChatMessage>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapState } from 'vuex'

import AppChatMessage from '@/common/app-chat/AppChatMessage.vue'
import AppButton from '@/design-system/buttons/AppButton.vue'
import { DocumentAISummaryOcrStatus } from "@/project/documents/ai-summary/DocumentsAISummaryOcrProcess.vue";

export default defineComponent({
  name: 'DocumentsAISummaryOcrProcessInfosMessages',
  components: {
    AppButton,
    AppChatMessage,
  },
  emits: ['launch-conversion'],
  props: {
    status: {
      type: String as PropType<DocumentAISummaryOcrStatus>,
      required: true,
    },
  },
  computed: {
    ...mapState('user', ['profile']),
    ...mapState('documentsAISummary', ['documentToSummarize']),
    ...mapGetters('room', ['roomMnemo']),
    learnMoreLink() {
      if (this.$i18n.locale === 'fr') {
        return 'https://help.closd.com/fr/knowledge-base/quest-ce-que-locr-et-comment-ca-fonctionne/'
      } else if (this.$i18n.locale === 'de') {
        return 'https://help.closd.com/en/knowledge-base/what-is-ocr-and-how-does-it-work/' //todo change
      }

      return 'https://help.closd.com/en/knowledge-base/what-is-ocr-and-how-does-it-work/'
    },
    processInfosTitle() {
      if (this.status === 'ocrPending' || this.status === 'summaryPending') {
        return this.$t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.processPending')
      }

      if (this.status === 'finished') {
        return this.$t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.hereIsYourSummary')
      }

      return this.$t('project.documents.ai-summary.DocumentsAISummaryOcrProcess.anErrorOccurred')
    },
    statusIsError() {
      return this.status === 'errorOnOcr' || this.status === 'errorOnSummary'
    },
  },
})
</script>

<style lang="scss">
a.learnMoreLink {
  color: #2671F2;
  text-decoration: underline;
}
</style>
